import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { StoryboardComment, CommentHistoryEntry } from './types';

interface Props {
  comments: StoryboardComment[];
  // Persist handlers — should call the API and update the parent's comment
  // state from the server response (the canonical list). They must RESOLVE on
  // success and REJECT/throw on failure so this component can surface an error.
  onAdd: (body: string) => Promise<void>;
  onUpdate: (commentId: string, body: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  disabled?: boolean;
}

// Format a single timestamp for display; '' if missing/unparseable.
function fmt(ts?: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// "Added <created>" plus "· edited <updated>" once the comment has been edited
// (updatedAt moves past createdAt).
function summaryTimestamp(c: StoryboardComment): string {
  const added = fmt(c.createdAt);
  if (!added) return '';
  const edited = !!c.updatedAt && c.updatedAt !== c.createdAt;
  return edited ? `Added ${added} · edited ${fmt(c.updatedAt)}` : `Added ${added}`;
}

const ACTION_LABEL: Record<string, string> = {
  created: 'Created',
  edited: 'Edited',
  deleted: 'Deleted',
};

// Comments / notes on a generated script: a list of editable, deletable notes
// plus an "add a note" box. Mirrors the TagEditor pattern — async handlers,
// server response is the source of truth — but for longer free-form text with
// per-item inline edit/delete. A single `busy` flag disables controls and
// shows a spinner during any in-flight mutation.
export default function CommentsSection({ comments, onAdd, onUpdate, onDelete, disabled = false }: Props) {
  const [newBody, setNewBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  // Comment ids whose edit-history timeline is expanded.
  const [historyOpen, setHistoryOpen] = useState<Record<string, boolean>>({});

  const toggleHistory = (commentId: string) =>
    setHistoryOpen(prev => ({ ...prev, [commentId]: !prev[commentId] }));

  const run = async (fn: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const body = newBody.trim();
    if (!body) return;
    if (await run(() => onAdd(body))) setNewBody('');
  };

  const startEdit = (c: StoryboardComment) => {
    setEditingId(c.commentId);
    setEditBody(c.body);
    setError(null);
  };

  const handleSaveEdit = async (commentId: string) => {
    const body = editBody.trim();
    if (!body) return;
    if (await run(() => onUpdate(commentId, body))) setEditingId(null);
  };

  const handleDelete = async (commentId: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this comment?')) return;
    await run(() => onDelete(commentId));
  };

  return (
    <Box>
      {comments.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No comments yet. Add a note below — e.g. edits to make to this script.
        </Typography>
      )}

      {comments.map(c => (
        <Box key={c.commentId} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {editingId === c.commentId ? (
            <Box>
              <TextField
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                fullWidth multiline minRows={2} maxRows={12}
                size="small" variant="outlined" disabled={busy} autoFocus
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Button size="small" variant="contained" disabled={busy || !editBody.trim()}
                  onClick={() => handleSaveEdit(c.commentId)}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
                  Save
                </Button>
                <Button size="small" disabled={busy} onClick={() => setEditingId(null)}>Cancel</Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.body}</Typography>
                  <Typography variant="caption" color="text.secondary">{summaryTimestamp(c)}</Typography>
                </Box>
                <IconButton size="small" disabled={disabled || busy} onClick={() => startEdit(c)} aria-label="Edit comment">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={disabled || busy} onClick={() => handleDelete(c.commentId)} aria-label="Delete comment">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {/* Edit timeline — only worth showing once there's more than the
                  initial "created" entry. */}
              {(c.history?.length || 0) > 1 && (
                <Box sx={{ mt: 0.5 }}>
                  <Button size="small" sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                    onClick={() => toggleHistory(c.commentId)}>
                    {historyOpen[c.commentId] ? 'Hide history' : `Show history (${c.history!.length})`}
                  </Button>
                  {historyOpen[c.commentId] && (
                    <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {(c.history as CommentHistoryEntry[]).map(h => (
                        <Box key={h.historyId} sx={{ mb: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {ACTION_LABEL[h.action] || h.action} · {fmt(h.createdAt)}
                          </Typography>
                          {h.body && h.action !== 'deleted' && (
                            <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
                              {h.body}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}

      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}

      <Box sx={{ mt: 1 }}>
        <TextField
          value={newBody}
          onChange={e => setNewBody(e.target.value)}
          placeholder="Add a note…"
          fullWidth multiline minRows={2} maxRows={12}
          size="small" variant="outlined" disabled={disabled || busy}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Button size="small" variant="contained" disabled={disabled || busy || !newBody.trim()}
            onClick={handleAdd}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
            Add comment
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
